import { AppSettings } from "@/AppSettings";
import { SourceDataEventType } from "@/api/dtos";
import SourceDataDetails from "@/components/details/SourceDataDetails";
import IsLoading from "@/components/layouts/IsLoading";
import Show from "@/components/layouts/Show";
import SourceDataItemAuditList from "@/components/lists/audit/SourceDataItemAuditList";
import NotesAndQueriesList from "@/components/lists/notes/NotesAndQueriesList";
import ConfigurationSourceDataContext from "@/contexts/data/ConfigurationSourceDataContext";
import CoordinatingGroupContext from "@/contexts/data/CoordinatingGroupContext";
import InstitutionContext from "@/contexts/data/InstitutionContext";
import MasterGroupContext from "@/contexts/data/MasterGroupContext";
import MetadataContext from "@/contexts/data/MetadataContext";
import ParticipantContext from "@/contexts/data/ParticipantContext";
import ParticipantNotesAndQueriesContext from "@/contexts/data/ParticipantNotesAndQueriesContext";
import SourceDataContext from "@/contexts/data/SourceDataContext";
import TrialConfigurationSourceDataContext from "@/contexts/data/TrialConfigurationSourceDataContext";
import TrialContext from "@/contexts/data/TrialContext";
import useParticipantSourceDataItemAudit from "@/hooks/data/useParticipantSourceDataItemAudit";
import useFormVersionConfiguration from "@/hooks/form/configuration/useFormVersionConfiguration";
import useEvent from "@/hooks/form/source-data/useEvent";
import useForm from "@/hooks/form/source-data/useForm";
import useItem from "@/hooks/form/source-data/useItem";
import useItemGroup from "@/hooks/form/source-data/useItemGroup";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Alert, AlertTitle, AppBar, Box, Button, Paper, Tab, Tabs, Typography, styled, tabClasses } from "@mui/material";
import { FC, useContext, useMemo } from "react";
import { Link, Outlet, useLocation, useParams } from "react-router-dom";

const TitleBox = styled(Box)(({theme}) => ({
    display: 'flex',
    marginBottom: theme.spacing(2), 
    fontSize: '1.5rem',
    gap: theme.spacing(2),
    alignItems: 'center'
}))

const ChipBox = styled(Box)(({theme}) => ({
    display: 'flex',
    marginLeft: 'auto',
    gap: theme.spacing(2),

    '& > div': {
        
        paddingLeft: theme.spacing(0.5),
    },
    '& svg': {
        fontSize: '1.5rem'
    }
}))

const Subtitle = styled(Typography)(({ theme }) => ({
    marginTop: theme.spacing(4),
    marginBottom: theme.spacing(2)
}));

const StyledTabs = styled(Tabs)(({ theme }) => ({
    minHeight: theme.spacing(6),

    [`& .${tabClasses.root}`]: {
        minHeight: theme.spacing(6),
        padding: theme.spacing(1.5, 2)
    }
}));

const ParticipantSourceDataItemValueAuditAndNotes: FC = () => {
    const { data: trial } = useContext(TrialContext);
    const { data: masterGroup } = useContext(MasterGroupContext);
    const { data: coordinatingGroup } = useContext(CoordinatingGroupContext);
    const { data: institution } = useContext(InstitutionContext);
    const { data: participant } = useContext(ParticipantContext);
    const { data: metadata } = useContext(MetadataContext);
    const { data: trialConfiguration } = useContext(TrialConfigurationSourceDataContext);
    const { data: configuration } = useContext(ConfigurationSourceDataContext);
    const { data: sourceData } = useContext(SourceDataContext);

    const { studyEventOid, studyEventRepeat, caseReportFormOid, itemGroupName, itemGroupRepeat, itemName } = useParams();

    const location = useLocation()
    

    const event = useEvent(
        metadata,
        trialConfiguration,
        configuration,
        sourceData,
        trial,
        masterGroup,
        coordinatingGroup,
        institution,
        participant,
        studyEventOid,
        +studyEventRepeat!
    )

    const form = useForm(
        metadata,
        trialConfiguration,
        configuration,
        sourceData,
        trial,
        masterGroup,
        coordinatingGroup,
        institution,
        participant,
        studyEventOid,
        +studyEventRepeat!,
        caseReportFormOid
    )

    const itemGroup = useItemGroup(
        metadata,
        trialConfiguration,
        configuration,
        sourceData,
        trial,
        masterGroup,
        coordinatingGroup,
        institution,
        participant,
        studyEventOid,
        +studyEventRepeat!,
        caseReportFormOid,
        form?.formVersion?.caseReportFormVersionOid,
        itemGroupName,
        itemGroupRepeat ? +itemGroupRepeat : undefined
    )

    const item = useItem(
        metadata,
        trialConfiguration,
        configuration,
        sourceData,
        trial,
        masterGroup,
        coordinatingGroup,
        institution,
        participant,
        studyEventOid,
        +studyEventRepeat!,
        caseReportFormOid,
        form?.formVersion?.caseReportFormVersionOid,
        itemGroupName,
        itemGroupRepeat ? +itemGroupRepeat : !itemGroupName ? 1 : undefined,
        itemName
    )
    
    console.log('Item', item);

    const allowed = useMemo(() => {

        if (!event || !form || !item) {
            return {
                configurationAllowReview: false,
                configurationAllowVerification: false,
                configurationAllowNotApplicable: false
            }
        }

        const configurationAllowReview = event?.configuration?.allowReview === true && 
            form?.configuration?.allowReview === true &&
            form.formVersion?.configuration?.allowReview === true &&
            (!itemGroup || itemGroup?.configuration?.allowReview === true) &&
            item?.configuration?.allowReview === true;

        const configurationAllowVerification = event?.configuration?.allowVerification === true && 
            form?.configuration?.allowVerification === true &&
            form.formVersion?.configuration?.allowVerification === true &&
            (!itemGroup || itemGroup?.configuration?.allowVerification === true) &&
            item?.configuration?.allowVerification === true;

        const configurationAllowNotApplicable = event?.configuration?.allowNotApplicable === true && 
            form?.configuration?.allowNotApplicable === true &&
            form.formVersion?.configuration?.allowNotApplicable === true &&
            (!itemGroup || itemGroup?.configuration?.allowNotApplicable === true) &&
            item?.configuration?.allowNotApplicable === true;

        return {
            configurationAllowReview,
            configurationAllowVerification,
            configurationAllowNotApplicable
        }
    }, [event, form, item, itemGroup]);

    console.log

    const value = useMemo(() => {
        if (location.pathname.includes('/notes-and-queries')) {
            return 0;
        }

        if (location.pathname.includes('/findings')) {
            return 1;
        }

        if (location.pathname.includes('/audit/data')) {
            return 2;
        }

        let index = 2;

        if (allowed.configurationAllowReview) {
            index++;
        }

        if (location.pathname.includes('/audit/review')) {
            return index;
        }

        if (allowed.configurationAllowVerification) {
            index++;
        }

        if (location.pathname.includes('/audit/verification')) {
            return index;
        }

        if (allowed.configurationAllowNotApplicable) {
            index++;
        }

        if (location.pathname.includes('/audit/not-applicable')) {
            return index;
        }
        
    }, [ location, allowed ])

    if (!trial) {
        return (
            <Alert
                severity="error"
            >
                <AlertTitle>No Trial</AlertTitle>
                No trial was found.
            </Alert>
        )
    }

    if (!participant) {
        return (
            <Alert
                severity="error"
            >
                <AlertTitle>No Participant</AlertTitle>
                No participant was found.
            </Alert>
        )
    }

    if (!metadata) {
        return (
            <Alert
                severity="error"
            >
                <AlertTitle>Trial Metadata Could Not Be Retrieved</AlertTitle>
                The metadata for the {trial.name} trial could not be retrieved from OpenClinica.
            </Alert>
        )
    }

    if (!trialConfiguration) {
        return (
            <Alert
                severity="error"
            >
                <AlertTitle>Trial Configuration Could Not Be Retrieved</AlertTitle>
                The global configuration for the {trial.name} trial could not be retrieved.
            </Alert>
        )
    }

    if (!configuration) {
        return (
            <Alert
                severity="error"
            >
                <AlertTitle>Allocated Configuration Could Not Be Retrieved</AlertTitle>
                The allocated configuration for participant {participant.studyNumber} on the {trial.name} trial could not be retrieved.
            </Alert>
        )
    }

    if (!sourceData) {
        return (
            <Alert
                severity="error"
            >
                <AlertTitle>Source Data Could Not Be Retrieved</AlertTitle>
                The source data for participant {participant.studyNumber} on the {trial.name} trial could not be retrieved.
            </Alert>
        )
    }

    if (!form?.formVersion?.caseReportFormVersionOid) {
        return (
            <Alert
                severity="error"
            >
                <AlertTitle>Case Report Form Version Could Not Be Retrieved</AlertTitle>
                The case report form version could not be retrieved.
            </Alert>
        )
    }
    
    const urlBase = `/${trial.oid}${institution ? `/institutions/${institution.code}` : ``}/participants/${participant.studyNumber}/events/${studyEventOid}/${studyEventRepeat}/forms/${caseReportFormOid}${itemGroupName ? `/${itemGroupName}/${itemGroupRepeat}` : ``}/${itemName}`;

    return (
        <>
            <TitleBox>
                <FontAwesomeIcon icon={AppSettings.icons.form} /> {event?.displayName}{event?.eventMetadata.type === SourceDataEventType.Repeating && <> &mdash; {event.studyEventRepeat}</>}: {form?.displayName}
            </TitleBox>
            <Subtitle variant="h2">
                Details
            </Subtitle>
            <Paper>
                <SourceDataDetails 
                    trialId={trial?.id}
                    masterGroupId={masterGroup?.id}
                    coordinatingGroupId={coordinatingGroup?.id}
                    institutionId={institution?.id}
                    participantId={participant?.id}
                    studyEventOid={studyEventOid}
                    studyEventRepeat={+studyEventRepeat!}
                    caseReportFormOid={caseReportFormOid}
                    caseReportFormVersionOid={form.formVersion.caseReportFormVersionOid}
                    itemGroupName={itemGroupName}
                    itemGroupRepeat={itemGroupRepeat ? +itemGroupRepeat! : 1}
                    itemName={itemName}

                    trial={trial}
                    masterGroup={masterGroup}
                    coordinatingGroup={coordinatingGroup}
                    institution={institution}
                    participant={participant}
                    metadata={metadata}
                    trialConfiguration={trialConfiguration}
                    configuration={configuration}
                    sourceData={sourceData}
                />
            </Paper>

            <AppBar position="static">
                <StyledTabs
                    value={value}
                    // onChange={handleChange}
                    indicatorColor="secondary"
                    textColor="inherit"
                    variant="fullWidth"
                >
                    <Tab 
                        label="Notes & Queries"
                        component={Link}
                        to={`${urlBase}/notes-and-queries`}
                    />
                    <Tab 
                        label="Findings"
                        component={Link}
                        to={`${urlBase}/findings`}
                    />
                    <Tab 
                        label="Data Audit"
                        component={Link}
                        to={`${urlBase}/audit/data`}
                    />
                    {
                        allowed.configurationAllowReview && (
                            <Tab 
                                label="Review Audit"
                                component={Link}
                                to={`${urlBase}/audit/review`}
                            />
                        )
                    }
                    {
                        allowed.configurationAllowVerification && (
                            <Tab 
                                label="Verification Audit"
                                component={Link}
                                to={`${urlBase}/audit/verification`}
                            />
                        )
                    }
                    {
                        allowed.configurationAllowNotApplicable && (
                            <Tab 
                                label="Not Applicable Audit"
                                component={Link}
                                to={`${urlBase}/audit/not-applicable`}
                            />
                        )
                    }
                </StyledTabs>
            </AppBar>
            <Outlet />
        </>
    )
};

export default ParticipantSourceDataItemValueAuditAndNotes;